/**
 * ExpensePage Component
 *
 * Complete expense tracking page with list view, create form,
 * edit/delete modals, receipt image upload, and filtering.
 * Restricted to admin/superadmin roles.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiUrl } from '../lib/api';
import { getLocalDateString } from '../lib/dateUtils';
import { getPreferredExtension } from '../lib/imageUtils';
import { useImages, getDisplayUrl } from '../hooks/useImages';
import { ImageGrid } from '../components/ui/ImageThumbnail';
import { ImageLightbox } from '../components/ui/ImageLightbox';
import { ImageUploader } from '../components/ui/ImageUploader';

// --- Interfaces ---

interface ExpenseCategory {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  createdAt: string;
  createdBy: string | null;
}

interface ExpenseEntry {
  id: string;
  userId: string;
  date: string;
  amount: string;
  categoryId: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  description: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  createdByName: string | null;
}

// --- Formatters ---

function formatRupiah(amount: string): string {
  const num = parseInt(amount, 10);
  if (isNaN(num)) return 'Rp 0';
  return 'Rp ' + num.toLocaleString('id-ID');
}

function formatDateIndonesian(date: string): string {
  return new Date(date).toLocaleDateString('id-ID', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function parseRupiahInput(value: string): string {
  return value.replace(/\D/g, '');
}

function formatInputAmount(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('id-ID');
}

// --- ExpenseCard Component ---

function ExpenseCard({
  expense,
  onEdit,
  onDelete,
}: {
  expense: ExpenseEntry;
  onEdit: (expense: ExpenseEntry) => void;
  onDelete: (expense: ExpenseEntry) => void;
}) {
  const { images } = useImages('expense', expense.id);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      {/* Header with Date, Creator, and Actions */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="text-sm text-gray-500">
            {formatDateIndonesian(expense.date)}
          </div>
          {expense.createdByName && (
            <div className="text-xs text-gray-400 mt-0.5">
              oleh {expense.createdByName}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => onEdit(expense)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
            title="Edit pengeluaran"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
            title="Hapus pengeluaran"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Category Badge + Amount Row */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          {expense.categoryColor && (
            <span
              className="w-3 h-3 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: expense.categoryColor }}
            />
          )}
          <span className="text-sm font-medium text-gray-700">
            {expense.categoryName || 'Tanpa Kategori'}
          </span>
        </div>
        <span className="text-lg font-bold text-gray-900">
          {formatRupiah(expense.amount)}
        </span>
      </div>

      {/* Description */}
      <p className="text-gray-800 text-sm">{expense.description}</p>

      {/* Notes */}
      {expense.notes && (
        <p className="text-sm text-gray-500 mt-1 italic">{expense.notes}</p>
      )}

      {/* Receipt Images */}
      {images.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <ImageGrid
            images={images}
            columns={3}
            size="sm"
            onImageClick={(index) => setLightboxIndex(index)}
          />
        </div>
      )}

      <ImageLightbox
        images={images}
        initialIndex={lightboxIndex ?? 0}
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-sm text-red-700 mb-2">Yakin ingin menghapus pengeluaran ini?</p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                onDelete(expense);
                setShowDeleteConfirm(false);
              }}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition min-h-11"
            >
              Hapus
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition min-h-11"
            >
              Batal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- CreateExpenseForm Component ---

function CreateExpenseForm({
  categories,
  onCategoryCreated,
  onExpenseCreated,
}: {
  categories: ExpenseCategory[];
  onCategoryCreated: (cat: ExpenseCategory) => void;
  onExpenseCreated: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [date, setDate] = useState(getLocalDateString());
  const [rawAmount, setRawAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Custom category inline add
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  // Deferred image upload
  const [uploaderKey, setUploaderKey] = useState(0);
  const pendingBlobsRef = useRef<Blob[]>([]);

  const handleAmountChange = (value: string) => {
    const digits = parseRupiahInput(value);
    setRawAmount(digits);
    setDisplayAmount(formatInputAmount(digits));
  };

  const handleCategorySelect = (value: string) => {
    if (value === '__add_new__') {
      setShowAddCategory(true);
      setCategoryId('');
    } else {
      setShowAddCategory(false);
      setCategoryId(value);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setAddingCategory(true);
    try {
      const res = await fetch(apiUrl('/api/expense-categories'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Gagal menambah kategori');
      }
      const created = await res.json();
      onCategoryCreated(created);
      setCategoryId(created.id);
      setNewCategoryName('');
      setShowAddCategory(false);
    } catch (err: any) {
      setError(err.message || 'Gagal menambah kategori');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rawAmount || !categoryId || !description.trim()) return;

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(apiUrl('/api/expenses'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          amount: rawAmount,
          categoryId,
          description: description.trim(),
          notes: notes.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Gagal menyimpan pengeluaran');
      }

      const created = await res.json();

      // Upload any pending images
      let imageUploadFailed = false;
      for (const blob of pendingBlobsRef.current) {
        try {
          const ext = await getPreferredExtension();
          const img = new Image();
          const url = URL.createObjectURL(blob);
          const dims = await new Promise<{ w: number; h: number }>((resolve) => {
            img.onload = () => {
              resolve({ w: img.naturalWidth, h: img.naturalHeight });
              URL.revokeObjectURL(url);
            };
            img.src = url;
          });

          const formData = new FormData();
          formData.append('file', blob, `image-${Date.now()}.${ext}`);
          formData.append('entryType', 'expense');
          formData.append('entryId', created.id);
          formData.append('width', dims.w.toString());
          formData.append('height', dims.h.toString());

          const uploadRes = await fetch(apiUrl('/api/images'), {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          if (!uploadRes.ok) {
            imageUploadFailed = true;
            console.error('Image upload failed:', uploadRes.status);
          }
        } catch (uploadErr) {
          imageUploadFailed = true;
          console.error('Image upload error:', uploadErr);
        }
      }
      if (imageUploadFailed) {
        setError('Pengeluaran tersimpan, tetapi sebagian foto gagal diunggah.');
      }

      // Reset form
      setDate(getLocalDateString());
      setRawAmount('');
      setDisplayAmount('');
      setCategoryId('');
      setDescription('');
      setNotes('');
      if (!imageUploadFailed) {
        setError(null);
      }
      pendingBlobsRef.current = [];
      setUploaderKey((k) => k + 1);
      onExpenseCreated();
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan pengeluaran');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mb-4">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="font-medium text-gray-700">Tambah Pengeluaran</span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <form onSubmit={handleSubmit} className="p-4 pt-2 border-t border-gray-100 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Date */}
          <div>
            <label htmlFor="expense-date" className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal
            </label>
            <input
              type="date"
              id="expense-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
              style={{ scrollMarginBottom: '12rem' }}
            />
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="expense-amount" className="block text-sm font-medium text-gray-700 mb-1">
              Jumlah (Rp)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">Rp</span>
              <input
                type="text"
                id="expense-amount"
                inputMode="numeric"
                value={displayAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="150.000"
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                style={{ scrollMarginBottom: '12rem' }}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="expense-category" className="block text-sm font-medium text-gray-700 mb-1">
              Kategori
            </label>
            <select
              id="expense-category"
              value={showAddCategory ? '__add_new__' : categoryId}
              onChange={(e) => handleCategorySelect(e.target.value)}
              required={!showAddCategory}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11 bg-white"
              style={{ scrollMarginBottom: '12rem' }}
            >
              <option value="">Pilih kategori...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
              <option value="__add_new__">+ Tambah Kategori</option>
            </select>

            {/* Inline category add */}
            {showAddCategory && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nama kategori baru"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11 text-sm"
                  style={{ scrollMarginBottom: '12rem' }}
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  disabled={addingCategory || !newCategoryName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 min-h-11"
                >
                  {addingCategory ? '...' : 'Tambah'}
                </button>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="expense-description" className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi
            </label>
            <textarea
              id="expense-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              required
              placeholder="Keterangan pengeluaran..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ scrollMarginBottom: '12rem' }}
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="expense-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Catatan <span className="text-gray-400 font-normal">(opsional)</span>
            </label>
            <textarea
              id="expense-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Catatan tambahan..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ scrollMarginBottom: '12rem' }}
            />
          </div>

          {/* Receipt Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Foto Struk <span className="text-gray-400 font-normal">(opsional)</span>
            </label>
            <ImageUploader
              key={uploaderKey}
              entryType="expense"
              onPendingBlobsChange={(blobs) => { pendingBlobsRef.current = blobs; }}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving || !rawAmount || !categoryId || !description.trim()}
            className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 min-h-12"
          >
            {saving ? 'Menyimpan...' : 'Simpan Pengeluaran'}
          </button>
        </form>
      )}
    </div>
  );
}

// --- EditExpenseModal Component ---

function EditExpenseModal({
  expense,
  categories,
  isOpen,
  onClose,
  onSave,
}: {
  expense: ExpenseEntry | null;
  categories: ExpenseCategory[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (expense: ExpenseEntry) => void;
}) {
  const [formData, setFormData] = useState({
    date: '',
    rawAmount: '',
    displayAmount: '',
    categoryId: '',
    description: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Images for this expense
  const { images: existingImages } = useImages('expense', expense?.id || '');
  const existingImageData = existingImages.map((img) => ({
    id: img.id,
    url: getDisplayUrl(img),
  }));

  // Populate form when expense changes
  useEffect(() => {
    if (expense) {
      const expenseDate = new Date(expense.date);
      setFormData({
        date: getLocalDateString(expenseDate),
        rawAmount: expense.amount,
        displayAmount: formatInputAmount(expense.amount),
        categoryId: expense.categoryId || '',
        description: expense.description || '',
        notes: expense.notes || '',
      });
      setError(null);
    }
  }, [expense]);

  const handleAmountChange = (value: string) => {
    const digits = parseRupiahInput(value);
    setFormData((prev) => ({
      ...prev,
      rawAmount: digits,
      displayAmount: formatInputAmount(digits),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expense) return;

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(apiUrl(`/api/expenses/${expense.id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: formData.date,
          amount: formData.rawAmount,
          categoryId: formData.categoryId,
          description: formData.description.trim(),
          notes: formData.notes.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Gagal menyimpan perubahan');
      }

      const updatedEntry = await res.json();
      // Merge category display fields back in since PATCH may not return them
      const selectedCategory = categories.find((c) => c.id === formData.categoryId);
      onSave({
        ...expense,
        ...updatedEntry,
        categoryName: selectedCategory?.name || expense.categoryName,
        categoryColor: selectedCategory?.color || expense.categoryColor,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan perubahan');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !expense) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Edit Pengeluaran</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Date */}
          <div>
            <label htmlFor="edit-expense-date" className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal
            </label>
            <input
              type="date"
              id="edit-expense-date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
            />
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="edit-expense-amount" className="block text-sm font-medium text-gray-700 mb-1">
              Jumlah (Rp)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">Rp</span>
              <input
                type="text"
                id="edit-expense-amount"
                inputMode="numeric"
                value={formData.displayAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="edit-expense-category" className="block text-sm font-medium text-gray-700 mb-1">
              Kategori
            </label>
            <select
              id="edit-expense-category"
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11 bg-white"
            >
              <option value="">Pilih kategori...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="edit-expense-description" className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi
            </label>
            <textarea
              id="edit-expense-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="edit-expense-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Catatan
            </label>
            <textarea
              id="edit-expense-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Receipt Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Foto Struk
            </label>
            <ImageUploader
              entryType="expense"
              entryId={expense.id}
              existingImages={existingImageData}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 min-h-11"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 min-h-11"
            >
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Main ExpensePage Component ---

export function ExpensePage() {
  const [expenses, setExpenses] = useState<ExpenseEntry[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState('0');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Users for filter
  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);

  // Filter input state
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');
  const [filterDisplayMinAmount, setFilterDisplayMinAmount] = useState('');
  const [filterDisplayMaxAmount, setFilterDisplayMaxAmount] = useState('');
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Applied filter state (only updates on Apply button click)
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [appliedCategoryId, setAppliedCategoryId] = useState('');
  const [appliedCreatedBy, setAppliedCreatedBy] = useState('');
  const [appliedMinAmount, setAppliedMinAmount] = useState('');
  const [appliedMaxAmount, setAppliedMaxAmount] = useState('');

  // Pagination
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Edit modal state
  const [editingExpense, setEditingExpense] = useState<ExpenseEntry | null>(null);

  // Fetch categories and users on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(apiUrl('/api/expense-categories'), {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    const fetchUsers = async () => {
      try {
        const res = await fetch(apiUrl('/api/users'), {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setUsersList((data.users || []).map((u: any) => ({ id: u.id, name: u.name })));
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchCategories();
    fetchUsers();
  }, []);

  // Fetch expenses
  const fetchExpenses = useCallback(
    async (resetOffset = false) => {
      setLoading(true);
      setError(null);

      const currentOffset = resetOffset ? 0 : offset;
      if (resetOffset) {
        setOffset(0);
      }

      try {
        const params = new URLSearchParams();
        params.append('limit', limit.toString());
        params.append('offset', currentOffset.toString());

        if (appliedStartDate) params.append('startDate', appliedStartDate);
        if (appliedEndDate) params.append('endDate', appliedEndDate);
        if (appliedCategoryId) params.append('categoryId', appliedCategoryId);
        if (appliedCreatedBy) params.append('createdBy', appliedCreatedBy);
        if (appliedMinAmount) params.append('minAmount', appliedMinAmount);
        if (appliedMaxAmount) params.append('maxAmount', appliedMaxAmount);

        const res = await fetch(apiUrl(`/api/expenses?${params.toString()}`), {
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error('Failed to fetch expenses');
        }

        const data = await res.json();

        if (resetOffset) {
          setExpenses(data.entries || []);
        } else {
          setExpenses((prev) => [...prev, ...(data.entries || [])]);
        }
        setTotal(data.total || 0);
        setTotalAmount(data.totalAmount || '0');
      } catch (err) {
        console.error('Failed to fetch expenses:', err);
        setError('Gagal memuat data. Silakan coba lagi.');
      } finally {
        setLoading(false);
      }
    },
    [offset, appliedStartDate, appliedEndDate, appliedCategoryId, appliedCreatedBy, appliedMinAmount, appliedMaxAmount, limit]
  );

  // Initial fetch and when applied filters change
  useEffect(() => {
    fetchExpenses(true);
  }, [appliedStartDate, appliedEndDate, appliedCategoryId, appliedCreatedBy, appliedMinAmount, appliedMaxAmount]);

  // Fetch more when offset changes
  useEffect(() => {
    if (offset > 0) {
      fetchExpenses(false);
    }
  }, [offset]);

  // Apply filters
  const handleApplyFilter = () => {
    setAppliedStartDate(filterStartDate);
    setAppliedEndDate(filterEndDate);
    setAppliedCategoryId(filterCategoryId);
    setAppliedCreatedBy(filterCreatedBy);
    setAppliedMinAmount(filterMinAmount);
    setAppliedMaxAmount(filterMaxAmount);
  };

  // Reset filters
  const handleResetFilter = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterCategoryId('');
    setFilterCreatedBy('');
    setFilterMinAmount('');
    setFilterMaxAmount('');
    setFilterDisplayMinAmount('');
    setFilterDisplayMaxAmount('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setAppliedCategoryId('');
    setAppliedCreatedBy('');
    setAppliedMinAmount('');
    setAppliedMaxAmount('');
  };

  // Load more
  const handleLoadMore = () => {
    setOffset((prev) => prev + limit);
  };

  // Handle delete
  const handleDelete = async (expense: ExpenseEntry) => {
    try {
      const res = await fetch(apiUrl(`/api/expenses/${expense.id}?confirm=true`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Gagal menghapus pengeluaran');
      }

      setExpenses((prev) => prev.filter((e) => e.id !== expense.id));
      setTotal((prev) => prev - 1);
      // Refetch to update totalAmount
      fetchExpenses(true);
    } catch (err: any) {
      setError(err.message || 'Gagal menghapus pengeluaran');
    }
  };

  // Handle edit save
  const handleSaveExpense = (updatedExpense: ExpenseEntry) => {
    setExpenses((prev) =>
      prev.map((e) => (e.id === updatedExpense.id ? updatedExpense : e))
    );
    // Refetch to update totalAmount
    fetchExpenses(true);
  };

  // Handle category created in the create form
  const handleCategoryCreated = (cat: ExpenseCategory) => {
    setCategories((prev) => [...prev, cat]);
  };

  const hasMore = expenses.length < total;
  const hasActiveFilter = appliedStartDate || appliedEndDate || appliedCategoryId || appliedCreatedBy || appliedMinAmount || appliedMaxAmount;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pengeluaran</h1>
        <p className="text-gray-600 mt-1">Catatan pengeluaran dan biaya</p>
      </div>

      {/* Total Amount Display */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">
            Total{hasActiveFilter ? ' (filter aktif)' : ''}
          </span>
          <span className="text-xl font-bold text-gray-900">
            {formatRupiah(totalAmount)}
          </span>
        </div>
      </div>

      {/* Create Form */}
      <CreateExpenseForm
        categories={categories}
        onCategoryCreated={handleCategoryCreated}
        onExpenseCreated={() => fetchExpenses(true)}
      />

      {/* Collapsible Filter Card */}
      <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
        {/* Filter Header */}
        <button
          type="button"
          onClick={() => setFilterExpanded(!filterExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="font-medium text-gray-700">Filter</span>
            {hasActiveFilter && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                Aktif
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${filterExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Filter Content */}
        {filterExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="filter-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Dari
                </label>
                <input
                  type="date"
                  id="filter-start-date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                />
              </div>
              <div>
                <label htmlFor="filter-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Sampai
                </label>
                <input
                  type="date"
                  id="filter-end-date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="filter-category" className="block text-sm font-medium text-gray-700 mb-1">
                Kategori
              </label>
              <select
                id="filter-category"
                value={filterCategoryId}
                onChange={(e) => setFilterCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11 bg-white"
              >
                <option value="">Semua kategori</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Created By */}
            <div>
              <label htmlFor="filter-created-by" className="block text-sm font-medium text-gray-700 mb-1">
                Dibuat oleh
              </label>
              <select
                id="filter-created-by"
                value={filterCreatedBy}
                onChange={(e) => setFilterCreatedBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11 bg-white"
              >
                <option value="">Semua pengguna</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Amount Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="filter-min-amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Min (Rp)
                </label>
                <input
                  type="text"
                  id="filter-min-amount"
                  inputMode="numeric"
                  value={filterDisplayMinAmount}
                  onChange={(e) => {
                    const digits = parseRupiahInput(e.target.value);
                    setFilterMinAmount(digits);
                    setFilterDisplayMinAmount(formatInputAmount(digits));
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                />
              </div>
              <div>
                <label htmlFor="filter-max-amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Max (Rp)
                </label>
                <input
                  type="text"
                  id="filter-max-amount"
                  inputMode="numeric"
                  value={filterDisplayMaxAmount}
                  onChange={(e) => {
                    const digits = parseRupiahInput(e.target.value);
                    setFilterMaxAmount(digits);
                    setFilterDisplayMaxAmount(formatInputAmount(digits));
                  }}
                  placeholder="10.000.000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleApplyFilter}
                className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition min-h-11"
              >
                Terapkan
              </button>
              <button
                type="button"
                onClick={handleResetFilter}
                className="px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition min-h-11"
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
          <button
            onClick={() => fetchExpenses(true)}
            className="ml-2 text-red-800 underline hover:no-underline"
          >
            Coba lagi
          </button>
        </div>
      )}

      {/* Loading State - Initial */}
      {loading && expenses.length === 0 && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && expenses.length === 0 && !error && (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <svg
            className="w-16 h-16 mx-auto text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-gray-500">Belum ada catatan pengeluaran</p>
        </div>
      )}

      {/* Expense List */}
      {expenses.length > 0 && (
        <div className="space-y-4">
          {expenses.map((expense) => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              onEdit={setEditingExpense}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && !loading && expenses.length > 0 && (
        <div className="mt-6 text-center">
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 text-blue-600 font-medium border border-blue-300 rounded-lg hover:bg-blue-50 transition min-h-11"
          >
            Muat lebih banyak
          </button>
        </div>
      )}

      {/* Loading More Indicator */}
      {loading && expenses.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="w-6 h-6 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Edit Expense Modal */}
      <EditExpenseModal
        expense={editingExpense}
        categories={categories}
        isOpen={editingExpense !== null}
        onClose={() => setEditingExpense(null)}
        onSave={handleSaveExpense}
      />
    </div>
  );
}
